import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { readAuthoredItem } from "../../../api/content/authoring/processAuthoringApi";
import { readAttachedChoices, type AttachedChoices } from "../../../api/content/searchApi";
import { useLoad, type LoadState } from "../../../shared/loading/useLoad";
import { termName, useProjectTermCards } from "../../ontology/terms/termNames";
import { facetsOf, type AttachedFacet } from "./attachedFilter";

export interface AttachedFilterChoices {
  /** The data classes attached to the project's documents, the most developed first. */
  readonly kinds: readonly string[];
  readonly facets: readonly AttachedFacet[];
  /** Documents whose attached statements say a text, for a search in them (F141). */
  readonly statedDocuments: number;
}

interface ReadChoices {
  readonly choices: AttachedChoices;
  readonly labelNames: ReadonlyMap<string, string>;
}

/**
 * What the project's filters can name (F88), with each label by its name in its label list and
 * each model term by its label at the version the server names (PC-5), through the one term-name
 * owner. A list that can't be read leaves its labels named by their keys; a term without a label,
 * or until the model's terms arrive, is named by the end of its IRI.
 */
export function useAttachedChoices(projectId: string): { readonly state: LoadState<AttachedFilterChoices>; readonly reload: () => void } {
  const { i18n } = useTranslation();
  const load = useCallback(async (signal: AbortSignal): Promise<ReadChoices> => {
    const choices = await readAttachedChoices(projectId, signal);
    const lists = [...new Set(choices.labels.map((label) => label.labelList))];
    const read = await Promise.all(lists.map((list) => readAuthoredItem(list, signal).catch(() => null)));
    const labelNames = new Map<string, string>();
    for (const item of read) {
      if (item?.content?.kind !== "label_list") continue;
      for (const label of item.content.definition.labels) labelNames.set(`${item.itemId}#${label.key}`, label.name);
    }
    return { choices, labelNames };
  }, [projectId]);
  const { state, reload } = useLoad(load);
  const read = state.status === "loaded" ? state.value : null;
  const cards = useProjectTermCards(projectId, read?.choices.termsNamedAt?.commit ?? null);
  const language = i18n.resolvedLanguage ?? "en";
  const named = useMemo((): LoadState<AttachedFilterChoices> => {
    if (state.status !== "loaded") return state;
    const { choices, labelNames } = state.value;
    return {
      status: "loaded",
      value: {
        kinds: choices.kinds.map((kind) => kind.kind),
        facets: facetsOf(choices, labelNames, (iri) => termName(iri, cards, language)),
        statedDocuments: choices.statedDocuments,
      },
    };
  }, [state, cards, language]);
  return { state: named, reload };
}
